import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, HandCoins, ShieldCheck, BadgeCheck, Clipboard, Stethoscope, Palette, Volleyball } from 'lucide-react';
import './HomePage.css';
import { programsPublicApi, testimonialsPublicApi, galleriesPublicApi } from '../../api/adminApi';
import imgone from '../../assets/images/imgone.svg';
import imgtwo from '../../assets/images/imgtwo.svg';
import imgthree from '../../assets/images/imgth.svg';
import welcomeimage from '../../assets/images/welcomeimage.jpg';
import heart from '../../assets/images/heartshape.svg';
import lightbulb from '../../assets/images/lightbulb.svg';
import creativeidea from '../../assets/images/creativegrowth.svg';
import users from '../../assets/images/users-profiles.svg';
import nature from '../../assets/images/nature.svg';
import google from '../../assets/images/google.svg';
import heroWave from '../../assets/images/basewaves.png';
import heroBgImage from '../../assets/images/hero-bg.svg';
import heroBgImageTwo from '../../assets/images/hero-bg-two.png';
import heroBgImageThree from '../../assets/images/hero-bg-three.png';
import { TESTIMONIALS } from '../../constants/testimonials';
import { galleryCoverUrl, galleryId, publicGalleryPath } from '../../pages/Admin/Galleries/galleryHelpers';
import heromain from '../../assets/images/bgtestmain.png';
import heromaintwo from '../../assets/images/3 3.png';
import heromainthree from '../../assets/images/image (3).png';
import mobileone from '../../assets/images/mobileone.png';
import mobiletwo from '../../assets/images/mobiletwo.png';
import mobilethree from '../../assets/images/mobilethree.png';  
import healthcare from '../../assets/images/healthcare.svg';
import  learning from '../../assets/images/learning.svg';
import creativegrowth from '../../assets/images/creativegrowth.svg';
import expert from '../../assets/images/users-profile.svg';

function getProgramLucideIcon(text = '') {
  const lower = text.toLowerCase();
  if (lower.includes('health') || lower.includes('medical') || lower.includes('nurse')) return <Stethoscope size={28} strokeWidth={1.8} />;
  if (lower.includes('art') || lower.includes('creat') || lower.includes('paint') || lower.includes('music')) return <Palette size={28} strokeWidth={1.8} />;
  if (lower.includes('sport') || lower.includes('physic') || lower.includes('gym') || lower.includes('soccer') || lower.includes('ball')) return <Volleyball size={28} strokeWidth={1.8} />;
  if (lower.includes('safe') || lower.includes('secur') || lower.includes('protect')) return <ShieldCheck size={28} strokeWidth={1.8} />;
  if (lower.includes('certif') || lower.includes('badge') || lower.includes('achiev')) return <BadgeCheck size={28} strokeWidth={1.8} />;
  if (lower.includes('fee') || lower.includes('financ') || lower.includes('tuition') || lower.includes('aid')) return <HandCoins size={28} strokeWidth={1.8} />;
  if (lower.includes('lesson') || lower.includes('assign') || lower.includes('curriculum') || lower.includes('clipboard')) return <Clipboard size={28} strokeWidth={1.8} />;
  return <BookOpen size={28} strokeWidth={1.8} />;
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return '1 week ago';
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return '1 month ago';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

/** Encode URLs that contain spaces (e.g. S3 object keys) for use in src/poster. */
function safeMediaUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const t = url.trim();
  if (!t) return '';
  try {
    return /\s/.test(t) ? encodeURI(t) : t;
  } catch {
    return t;
  }
}

/**
 * Only use a real uploaded testimonial video. No bundled “demo” MP4 fallbacks.
 * If the API echoes the same URL as photo or avatar, treat as no video.
 */
function getTestimonialVideoUrl(testimonial) {
  const v = safeMediaUrl(testimonial?.video_url);
  if (!v) return '';
  const photo = safeMediaUrl(testimonial?.photo_url);
  const avatar = safeMediaUrl(testimonial?.avatar_url);
  if (photo && v === photo) return '';
  if (avatar && v === avatar) return '';
  return v;
}

function testimonialVideoAriaLabel(testimonial) {
  if (getTestimonialVideoUrl(testimonial)) {
    return testimonial.name ? `Video testimonial from ${testimonial.name}` : 'Testimonial video';
  }
  return testimonial.name ? `Testimonial from ${testimonial.name}` : 'Testimonial';
}

function HomePage() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const videoWrapRef = useRef(null);
  const progressRef = useRef(null);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => {
      if (video.duration) {
        setProgress((video.currentTime / video.duration) * 100);
        setCurrentTime(formatTime(video.currentTime));
      }
    };
    const onLoadedMetadata = () => {
      setDuration(formatTime(video.duration));
    };
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);

    // Ensure autoplay works — retry if browser blocks it
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked — keep state as paused
        setIsPlaying(false);
      });
    }

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const toggleFullscreen = () => {
    const wrap = videoWrapRef.current;
    if (!wrap) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
    } else {
      (wrap.requestFullscreen || wrap.webkitRequestFullscreen).call(wrap);
    }
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
  };

  const slides = React.useMemo(
    () => [
      {
        id: 'slide-1',
        theme: 'theme-cream',
        badge: 'Loved by 5,000+ Parents',
        title: 'A Bright Beginning\nStarts Here',
        desc:
          'Nurturing young minds through play, discovery, and structured early education in a safe, joyful environment where every child thrives.',
        primaryCta: { to: { pathname: '/admissions', hash: 'schedule-tour' }, label: 'Schedule Tour' },
        secondaryCta: { to: '/contact', label: 'Inquiry Now' },
        image:
          'https://images.unsplash.com/photo-1546015720-b8b30df5aa27?w=1200&q=80&auto=format&fit=crop',
        imageAlt: 'Child playing with toy airplane',
      },
      {
        id: 'slide-2',
        theme: 'theme-sky',
        badge: 'Play. Learn. Thrive.',
        title: 'Where Curiosity\nBecomes Confidence',
        desc:
          'A warm, structured environment that builds foundational skills through hands-on learning, creativity, and care.',
        primaryCta: { to: { pathname: '/admissions', hash: 'schedule-tour' }, label: 'Schedule Tour' },
        secondaryCta: { to: '/programs', label: 'Explore Programs' },
        image:
          'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80&auto=format&fit=crop',
        imageAlt: 'Child learning with teacher',
      },
      {
        id: 'slide-3',
        theme: 'theme-sand',
        badge: 'Trusted Early Education',
        title: 'A Safe Place\nTo Grow & Discover',
        desc:
          'From preschool to afterschool, we support every child with engaging activities and expert guidance.',
        primaryCta: { to: { pathname: '/admissions', hash: 'schedule-tour' }, label: 'Schedule Tour' },
        secondaryCta: { to: '/contact', label: 'Contact Us' },
        image:
          'https://images.unsplash.com/photo-1516627145497-ae6968895b74?w=1200&q=80&auto=format&fit=crop',
        imageAlt: 'Children playing together',
      },
    ],
    []
  );

  const heroBackgroundImages = React.useMemo(
    () => [heroBgImage, heroBgImageTwo, heroBgImageThree],
    []
  );

  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState('');

  const [homeGalleries, setHomeGalleries] = useState([]);
  const [homeGalleriesLoaded, setHomeGalleriesLoaded] = useState(false);

  const homeGalleriesWithIds = React.useMemo(
    () => homeGalleries.filter((g) => Boolean(galleryId(g))),
    [homeGalleries]
  );

  // API testimonials state – falls back to static TESTIMONIALS if API returns nothing
  const [apiTestimonials, setApiTestimonials] = useState([]);
  const testimonialsData = apiTestimonials.length > 0 ? apiTestimonials : TESTIMONIALS;
  const testimonialsDataRef = React.useRef(testimonialsData);
  React.useEffect(() => { testimonialsDataRef.current = testimonialsData; }, [testimonialsData]);

  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const [playingTestimonialIndex, setPlayingTestimonialIndex] = useState(null);
  const testimonialVideoRefs = React.useRef([]);
  const [testimonialModalIndex, setTestimonialModalIndex] = React.useState(null);
  const suppressTestimonialClickRef = React.useRef(false);

  const goToTestimonial = React.useCallback(
    (nextIndex) => {
      const total = testimonialsDataRef.current.length;
      setActiveTestimonialIndex(((nextIndex % total) + total) % total);
    },
    []
  );

  const goPrevTestimonial = React.useCallback(
    () => goToTestimonial(activeTestimonialIndex - 1),
    [activeTestimonialIndex, goToTestimonial]
  );

  const goNextTestimonial = React.useCallback(
    () => goToTestimonial(activeTestimonialIndex + 1),
    [activeTestimonialIndex, goToTestimonial]
  );

  useEffect(() => {
    setProgramsLoading(true);
    setProgramsError('');
    programsPublicApi
      .list(1, 3)
      .then((res) => setPrograms((res.list || []).slice(0, 3)))
      .catch((err) => setProgramsError(err?.userMessage || err?.message || 'Failed to load programs'))
      .finally(() => setProgramsLoading(false));
  }, []);

  useEffect(() => {
    testimonialsPublicApi
      .list(1, 10)
      .then((res) => {
        if (res.list && res.list.length > 0) {
          setApiTestimonials(
            res.list.map((t) => ({
              id: t.id,
              name: t.name || '',
              quote: t.review || '',
              time: formatRelativeTime(t.created_at),
              rating: t.rating ?? 5,
              avatar_url: t.avatar_url ?? t.avatarUrl ?? null,
              photo_url: t.photo_url ?? t.photoUrl ?? null,
              video_url: t.video_url ?? t.videoUrl ?? null,
              is_verified: t.is_verified ?? t.isVerified,
              is_featured: t.is_featured ?? t.isFeatured,
            }))
          );
        }
      })
      .catch(() => {}); // silently fall back to static data
  }, []);

  useEffect(() => {
    let cancelled = false;
    setHomeGalleriesLoaded(false);
    galleriesPublicApi
      .list(1, 8)
      .then((res) => {
        if (!cancelled) setHomeGalleries(res.list || []);
      })
      .catch(() => {
        if (!cancelled) setHomeGalleries([]);
      })
      .finally(() => {
        if (!cancelled) setHomeGalleriesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ensure we don't keep multiple testimonial videos playing while navigating.
  useEffect(() => {
    testimonialVideoRefs.current.forEach((v) => {
      try {
        if (v && !v.paused) v.pause();
      } catch {
        // no-op
      }
    });
    setPlayingTestimonialIndex(null);
  }, [activeTestimonialIndex]);

  const openTestimonialModal = React.useCallback((idx) => {
    // Pause inline video before opening modal.
    const v = testimonialVideoRefs.current[idx];
    try {
      if (v && !v.paused) v.pause();
    } catch {
      // no-op
    }
    setPlayingTestimonialIndex(null);
    setTestimonialModalIndex(idx);
  }, []);

  const closeTestimonialModal = React.useCallback(() => {
    setTestimonialModalIndex(null);
  }, []);

  const toggleTestimonialVideo = React.useCallback((idx) => {
    const video = testimonialVideoRefs.current[idx];
    if (!video) return;

    if (video.paused) {
      // Pause any other playing testimonial videos.
      testimonialVideoRefs.current.forEach((v, i) => {
        if (v && i !== idx && !v.paused) v.pause();
      });
      video.play().then(() => setPlayingTestimonialIndex(idx)).catch(() => setPlayingTestimonialIndex(idx));
    } else {
      video.pause();
      setPlayingTestimonialIndex(null);
    }
  }, []);

  return (
    <main className="home-page">
      {/* Hero (Bootstrap 5 carousel with captions) */}
      {/* <section className="hero" aria-roledescription="carousel" aria-label="Homepage hero carousel">
        <div
          id="carouselExampleCaptions"
          className="carousel slide carousel-fade"
          data-bs-ride="carousel"
          data-bs-interval="5000"
          data-bs-pause="false"
        >
          <div className="carousel-indicators">
            {slides.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                data-bs-target="#carouselExampleCaptions"
                data-bs-slide-to={idx}
                className={idx === 0 ? 'active' : ''}
                aria-current={idx === 0 ? 'true' : undefined}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>

          <div className="carousel-inner">
            {slides.map((s, idx) => (
              <div key={s.id} className={`carousel-item ${idx === 0 ? 'active' : ''} ${s.theme}`}>
                <img
                  src={heroBackgroundImages[idx] || heroBgImage}
                  className="d-block w-100 hero-carousel-img"
                  alt=""
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                />
                <div className="hero-overlay" aria-hidden="true" />

                <div className="carousel-caption hero-carousel-caption">
                  <div className="hero-inner">
                    <div className="hero-grid">
                      <div className="hero-content">
                        <p className="hero-badge">{s.badge}</p>
                        <h1 className="hero-title">
                          {s.title.split('\n').map((line, lineIdx) => (
                            <React.Fragment key={`${s.id}-title-${lineIdx}`}>
                              {line}
                              {lineIdx === 0 ? <br /> : null}
                            </React.Fragment>
                          ))}
                        </h1>
                        <p className="hero-desc">{s.desc}</p>

                        <div className="hero-buttons">
                          <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary">
                            <span className="btn-icon" aria-hidden="true">
                              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
                                <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                              </svg>
                            </span>
                            Schedule Tour
                          </Link>
                          <Link to="/programs" className="btn btn-outline">
                            Explore Programs
                          </Link>
                        </div>

                        <div className="hero-features">
                          <div className="hero-feature">
                            <span className="hero-feature-icon" aria-hidden="true">
                              <img src={imgone} alt="" />
                            </span>
                            <span className="hero-feature-title">Licensed</span>
                            <span className="hero-feature-sub">NYC Certified</span>
                          </div>
                          <div className="hero-feature">
                            <span className="hero-feature-icon" aria-hidden="true">
                              <img src={imgtwo} alt="" />
                            </span>
                            <span className="hero-feature-title">4.9 Rating</span>
                            <span className="hero-feature-sub">300+ Reviews</span>
                          </div>
                          <div className="hero-feature">
                            <span className="hero-feature-icon" aria-hidden="true">
                              <img src={imgthree} alt="" />
                            </span>
                            <span className="hero-feature-title">500+</span>
                            <span className="hero-feature-sub">Family Served</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>

        <div className="hero-wave" aria-hidden="true">
          <img src={heroWave} alt="" />
        </div>
      </section>
       */}
      <div
        id="carouselExampleCaptions"
        className="hero carousel slide"
        data-bs-ride="carousel"
        data-bs-interval="false"
      >
        <div className="carousel-indicators">
          <button type="button" data-bs-target="#carouselExampleCaptions" data-bs-slide-to="0" className="active" aria-current="true" aria-label="Slide 1"></button>
          <button type="button" data-bs-target="#carouselExampleCaptions" data-bs-slide-to="1" aria-label="Slide 2"></button>
          <button type="button" data-bs-target="#carouselExampleCaptions" data-bs-slide-to="2" aria-label="Slide 3"></button>
        </div>
        <div className="carousel-inner">
          <div className="carousel-item active">
            <img src={heromain} className="d-block w-100 main-bg" alt="..." />
            <img src={mobileone} className="d-block d-md-none" alt="..." />
            <div className="carousel-caption d-none d-md-block">
              <p className="hero-badge">Loved by 5,000+ Parents</p>
              <h1 className="hero-title">
                A Bright Beginning
                Starts Here
              </h1>
              <p className="hero-desc">Nurturing young minds through play, discovery, and structured early education in a safe, joyful environment where every child thrives.</p>
              <div className="hero-buttons">
                <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary">
                  <span className="btn-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                    </svg>
                  </span>
                  Schedule Tour
                </Link>
                <Link to="/programs" className="btn btn-outline">
                  Apply Now
                </Link>
              </div>
              <div className="hero-features">
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgone} alt="" />
                  </span>
                  <span className="hero-feature-title">Licensed</span>
                  <span className="hero-feature-sub">NYC Certified</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgtwo} alt="" />
                  </span>
                  <span className="hero-feature-title">4.9 Rating</span>
                  <span className="hero-feature-sub">300+ Reviews</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgthree} alt="" />
                  </span>
                  <span className="hero-feature-title">500+</span>
                  <span className="hero-feature-sub">Family Served</span>
                </div>
              </div>
            </div>
          </div>
          <div className="carousel-item">
            <img src={heromaintwo} className="d-block w-100 main-bg" alt="..." />
            <img src={mobiletwo} className="d-block d-md-none" alt="..." />
            <div className="carousel-caption d-none d-md-block">
              <p className="hero-badge">Play. Learn. Thrive.</p>
              <h1 className="hero-title">
              Where Curiosity
              Becomes Confidence
              </h1>
              <p className="hero-desc">A warm, structured environment that builds foundational skills through hands-on learning, creativity, and care.</p>
              <div className="hero-buttons">
                <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary">
                  <span className="btn-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                    </svg>
                  </span>
                  Schedule Tour
                </Link>
                <Link to="/programs" className="btn btn-outline">
                  Explore Programs
                </Link>
              </div>
              <div className="hero-features">
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgone} alt="" />
                  </span>
                  <span className="hero-feature-title">Licensed</span>
                  <span className="hero-feature-sub">NYC Certified</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgtwo} alt="" />
                  </span>
                  <span className="hero-feature-title">4.9 Rating</span>
                  <span className="hero-feature-sub">300+ Reviews</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgthree} alt="" />
                  </span>
                  <span className="hero-feature-title">500+</span>
                  <span className="hero-feature-sub">Family Served</span>
                </div>
              </div>
            </div>
          </div>
          <div className="carousel-item">
            <img src={heromainthree} className="d-block w-100 main-bg" alt="..." />
            <img src={mobilethree} className="d-block d-md-none" alt="..." />
            <div className="carousel-caption d-none d-md-block">
              <p className="hero-badge">
             Trusted Early Education
              </p>
              <h1 className="hero-title">
              A Safe Place
              To Grow & Discover  
              </h1>
              <p className="hero-desc">From preschool to afterschool, we support every child with engaging activities and expert guidance.</p>
              <div className="hero-buttons">
                <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary">
                  <span className="btn-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                    </svg>
                  </span>
                  Schedule Tour
                </Link>
                <Link to="/programs" className="btn btn-outline">
                 Contact Us
                </Link>
              </div>
              <div className="hero-features">
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgone} alt="" />
                  </span>
                  <span className="hero-feature-title">Licensed</span>
                  <span className="hero-feature-sub">NYC Certified</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgtwo} alt="" />
                  </span>
                  <span className="hero-feature-title">4.9 Rating</span>
                  <span className="hero-feature-sub">300+ Reviews</span>
                </div>
                <div className="hero-feature">
                  <span className="hero-feature-icon" aria-hidden="true">
                    <img src={imgthree} alt="" />
                  </span>
                  <span className="hero-feature-title">500+</span>
                  <span className="hero-feature-sub">Family Served</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <button className="carousel-control-prev" type="button" data-bs-target="#carouselExampleCaptions" data-bs-slide="prev">
    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
    <span className="visually-hidden">Previous</span>
  </button>
  <button className="carousel-control-next" type="button" data-bs-target="#carouselExampleCaptions" data-bs-slide="next">
    <span className="carousel-control-next-icon" aria-hidden="true"></span>
    <span className="visually-hidden">Next</span>
  </button> */}
      </div>

      {/* Welcome */}
      <section className="welcome" id="about">
        <div className="welcome-inner">
          <div className="welcome-left">
            <div className="welcome-image-wrap" style={{ position: 'relative' }} ref={videoWrapRef}>
              <video
                ref={videoRef}
                src="/videos/David Foster.mov"
                className="welcome-image"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
              />
              <div className="video-controls">
                <button className="video-control-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
                  {isPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
                <button className="video-control-btn" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
                  {isMuted ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                  )}
                </button>
                <span className="video-time">{currentTime} / {duration}</span>
                <div className="video-progress" ref={progressRef} onClick={handleProgressClick}>
                  <div className="video-progress-filled" style={{ width: `${progress}%` }} />
                </div>
                <button className="video-control-btn" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                  {isFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                  )}
                </button>
              </div>
            </div>
            <div className="welcome-stats">
              <div className="welcome-stat">
                <div className="welcome-stat-value">15+</div>
                <div className="welcome-stat-label">Years</div>
              </div>
              <div className="welcome-stat">
                <div className="welcome-stat-value">3</div>
                <div className="welcome-stat-label">Programs</div>
              </div>
              <div className="welcome-stat">
                <div className="welcome-stat-value">20+</div>
                <div className="welcome-stat-label">Teachers</div>
              </div>
            </div>
          </div>
          <div className="welcome-right">
            <h2 className="welcome-title">
              WELCOME TO THE DAVID FOSTER PRESCHOOL &amp; AFTERSCHOOL ACADEMY
            </h2>
            <p className="welcome-desc">
              We provide a caring, engaging space where children build confidence, friendships,
              and foundational learning skills that last a lifetime. Our approach combines
              structured academics with hands-on play to support the whole child&apos;s development.
            </p>
            <div className="welcome-grid">
              <div className="welcome-card">
                <span className="welcome-card-icon" aria-hidden="true"><img src={healthcare} alt="" /></span>
                <div className="welcome-card-text">
                  <h3>Nurturing Care</h3>
                  <p>Every child receives individual attention in a warm, supportive environment.</p>
                </div>
              </div>
              <div className="welcome-card">
                <span className="welcome-card-icon" aria-hidden="true"><img src={learning} alt="" /></span>
                <div className="welcome-card-text">
                  <h3>Play-Based Learning</h3>
                  <p>Engaging curriculum that makes learning an exciting adventure.</p>
                </div>
              </div>
              <div className="welcome-card">
                <span className="welcome-card-icon" aria-hidden="true"><img src={expert} alt="" /></span>
                <div className="welcome-card-text">
                  <h3>Expert Teachers</h3>
                  <p>Certified educators passionate about early childhood development.</p>
                </div>
              </div>
              <div className="welcome-card">
                <span className="welcome-card-icon" aria-hidden="true"><img src={creativegrowth}alt="" /></span>
                <div className="welcome-card-text">
                  <h3>Creative Growth</h3>
                  <p>Arts, music, and movement to spark imagination and creativity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="programs" id="programs">
        <div className="programs-inner">
          <p className="section-eyebrow">What we offer</p>
          <h2 className="section-title">Programs for Every Stage</h2>
          <p className="section-desc">Age-appropriate learning designed to prepare children for lifelong success and a love of learning.</p>
          {programsLoading && (
            <p className="programs-status programs-status--loading" role="status">Loading programs…</p>
          )}
          {programsError && (
            <p className="programs-status programs-status--error" role="alert">{programsError}</p>
          )}
          {!programsLoading && !programsError && (
            <div className="program-cards">
              {programs.map((p, idx) => {
                const min = p.age_min ?? p.ageMin;
                const max = p.age_max ?? p.ageMax;
                let ageGroup = '—';
                if (min != null && max != null && max > 0) ageGroup = `Ages ${min}–${max} years`;
                else if (min != null) ageGroup = `Age ${min}`;
                const title = p.name || p.title || p.programName || 'Program';
                const description = p.description || p.desc || '';
                const features = [];
                if (p.feature_timeline) features.push(p.feature_timeline);
                if (p.feature_ratio) features.push(p.feature_ratio);
                const programText = [title, description, ...features].filter(Boolean).join(' ');
                return (
                  <article key={p.id ?? idx} className="program-card">
                    <div className="program-card-header">
                      <span className="program-card-icon" aria-hidden="true">
                        {p.icon_url
                          ? <img src={p.icon_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                          : getProgramLucideIcon(programText)}
                      </span>
                    </div>
                    <h3 className="program-title">{title}</h3>
                    <p className="program-age">{ageGroup}</p>
                    <p className="program-desc">{description}</p>
                    {features.length > 0 && (
                      <ul>
                        {features.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    )}
                    <Link to="/programs" className="program-link">
                      Learn More <span aria-hidden="true">→</span>
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
          <Link to="/programs" className="programs-cta">
            View All Program
          </Link>
        </div>
      </section>

      {/* Philosophy */}
      <section className="philosophy">
        <div className="philosophy-inner">
          <div className="philosophy-left">
            <p className="section-eyebrow">Our Philosophy</p>
            <h2 className="philosophy-title">
              Learning Through<br />Play &amp; Purpose
            </h2>
            <p className="philosophy-intro">
              Our curriculum blends structured academics with hands-on play to support the whole child—emotionally, socially, physically, and intellectually. We believe children learn best when they&apos;re engaged, curious, and having fun.
            </p>
            <div className="philosophy-quote">
              <blockquote>
                &quot;Every child has the ability to learn. Every child learns at his/her pace. It depends upon the educator to determine the pace of each child.&quot;
              </blockquote>
              <cite>— Mrs. Shelia M. Foster-Golding, Lead Educator</cite>
            </div>
            {/* <a href="#approach" className="btn btn-primary philosophy-btn">Our Approach</a> */}
          </div>
          <div className="philosophy-right">
            <div className="philosophy-card philosophy-card-blue">
              <span className="philosophy-card-icon" aria-hidden="true"><img src={lightbulb} alt="" /></span>
              <h3>Curiosity-Driven</h3>
              <p>We encourage questions and exploration, making every moment a learning opportunity.</p>
            </div>
            <div className="philosophy-card philosophy-card-yellow">
              <span className="philosophy-card-icon" aria-hidden="true"><img src={creativeidea} alt="" /></span>
              <h3>Creative Expression</h3>
              <p>Art, music, and imaginative play help children discover their unique voices.</p>
            </div>
            <div className="philosophy-card philosophy-card-blue">
              <span className="philosophy-card-icon" aria-hidden="true"><img src={users} alt="" /></span>
              <h3>Social Development</h3>
              <p>Building friendships, empathy, and cooperation through guided group activities.</p>
            </div>
            <div className="philosophy-card philosophy-card-yellow">
              <span className="philosophy-card-icon" aria-hidden="true"><img src={nature} alt="" /></span>
              <h3>Nature Connection</h3>
              <p>Outdoor play and environmental awareness foster respect for our world.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety */}
      <section className="safety">
        <div className="safety-bg" aria-hidden="true" />
        <div className="safety-inner">
          <p className="section-eyebrow">Your Child&apos;s Safety is Our Priority</p>
          <h2 className="section-title">Safe. Secure. Trusted.</h2>
          <p className="section-desc">We maintain the highest standards of safety and care to give you peace of mind.</p>
          <div className="safety-grid">
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><BookOpen size={24} strokeWidth={2} /></span>
              <h3>CBO – NYC Department of Education</h3>
              <p>Fully licensed by NYC Department of Health and Mental Hygiene.</p>
            </div>
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><HandCoins size={24} strokeWidth={2} /></span>
              <h3>Daily Sanitization</h3>
              <p>Rigorous cleaning protocols to maintain a healthy environment.</p>
            </div>
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><ShieldCheck size={24} strokeWidth={2} /></span>
              <h3>Secure Entry</h3>
              <p>Controlled access with video monitoring and sign-in/out procedures.</p>
            </div>
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><BadgeCheck size={24} strokeWidth={2} /></span>
              <h3>Background Checked</h3>
              <p>All staff undergo thorough background checks and training.</p>
            </div>
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><Clipboard size={24} strokeWidth={2} /></span>
              <h3>CPR Certified</h3>
              <p>Teachers trained in first aid and emergency response.</p>
            </div>
            <div className="safety-card">
              <span className="safety-card-icon" aria-hidden="true"><Stethoscope size={24} strokeWidth={2} /></span>
              <h3>Health Screenings</h3>
              <p>Daily wellness checks for all children and staff.</p>
            </div>
          </div>
          <Link to="/health-safety" className="btn btn-primary safety-cta">Learn about our safety protocol</Link>
        </div>
      </section>

      {/* Gallery — only live data from GET /galleries (no static demo tiles) */}
      <section className="gallery" aria-labelledby="home-gallery-heading">
        <div className="gallery-inner">
          <p className="section-eyebrow">Gallery</p>
          <h2 id="home-gallery-heading" className="section-title">
            Life At Our Preschool
          </h2>
          <p className="section-desc">Peek into our classrooms and see the joy of learning in action.</p>
          {!homeGalleriesLoaded ? (
            <div
              className="gallery-grid gallery-grid--loading"
              aria-busy="true"
              aria-label="Loading gallery previews"
            >
              <div className="gallery-skeleton-main" />
              <div className="gallery-skeleton-thumbs">
                <div className="gallery-skeleton-thumb" />
                <div className="gallery-skeleton-thumb" />
                <div className="gallery-skeleton-thumb" />
                <div className="gallery-skeleton-thumb" />
              </div>
            </div>
          ) : homeGalleriesWithIds.length > 0 ? (
            <div
              className={`gallery-grid${homeGalleriesWithIds.length === 1 ? ' gallery-grid--single' : ''}`}
            >
              {(() => {
                const featured = homeGalleriesWithIds[0];
                const featuredId = galleryId(featured);
                const featuredCover = galleryCoverUrl(featured);
                return (
                  <Link to={publicGalleryPath(featuredId)} className="gallery-main">
                    {featuredCover ? (
                      <img
                        src={featuredCover}
                        alt={
                          featured.title ? `${featured.title} — featured` : 'Gallery featured photo'
                        }
                      />
                    ) : (
                      <div
                        className="gallery-cover-placeholder"
                        role="img"
                        aria-label={
                          featured.title
                            ? `${featured.title} — no preview image yet`
                            : 'Gallery preview not available'
                        }
                      />
                    )}
                    <h3 className="gallery-main-title">{featured.title || 'Gallery'}</h3>
                  </Link>
                );
              })()}
              {homeGalleriesWithIds.length > 1 ? (
                <div className="gallery-thumbs">
                  {homeGalleriesWithIds.slice(1, 5).map((g) => {
                    const gid = galleryId(g);
                    const url = galleryCoverUrl(g);
                    return (
                      <Link key={gid} to={publicGalleryPath(gid)} className="gallery-thumb">
                        {url ? (
                          <img
                            src={url}
                            alt={g.title ? `${g.title} — preview` : 'Gallery preview'}
                          />
                        ) : (
                          <div
                            className="gallery-thumb-placeholder"
                            role="img"
                            aria-label={
                              g.title ? `${g.title} — no preview image yet` : 'Gallery preview not available'
                            }
                          />
                        )}
                        <h3 className="gallery-thumb-title">{g.title || 'Gallery'}</h3>
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="gallery-home-empty">
              New photos will show here when galleries are published. You can still open the full gallery page
              anytime.
            </p>
          )}
          <Link to="/gallery" className="btn btn-primary gallery-cta">
            View All Gallery
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials" aria-label="Testimonials carousel">
        <div className="testimonials-inner">
          <div className="testimonials-header">
            <div>
              <p className="section-eyebrow">Testimonials</p>
              <h2 className="section-title">Loved by Families</h2>
              <p className="section-desc">Don&apos;t just take our word for it—hear from the families we serve.</p>
            </div>
            <div className="testimonials-google">
              <div className="testimonials-google-top" aria-hidden="true">
                <span className="testimonials-google-mark"><img src={google} alt="" /></span>
                <div className="testimonials-stars">
                  <p className="testimonials-stars-rating">★★★★★</p>
                  <p>Based on 500+ reviews</p>
                </div>
              </div>

            </div>
          </div>

          <div className="testimonial-carousel">
            <button
              type="button"
              className="testimonial-arrow testimonial-arrow-prev"
              aria-label="Previous testimonial"
              onClick={goPrevTestimonial}
            >
              ‹
            </button>
            <button
              type="button"
              className="testimonial-arrow testimonial-arrow-next"
              aria-label="Next testimonial"
              onClick={goNextTestimonial}
            >
              ›
            </button>

            <div
              className="testimonial-cards-track"
              style={{
                transform: `translateX(-${activeTestimonialIndex * (100 / testimonialsData.length)}%)`,
                width: `${testimonialsData.length * 100}%`,
              }}
            >
              {testimonialsData.map((t, idx) => {
                const vSrc = getTestimonialVideoUrl(t);
                const hasVideo = Boolean(vSrc);
                const vPoster = safeMediaUrl(t.photo_url) || safeMediaUrl(t.avatar_url) || undefined;
                const showBodyAvatar =
                  Boolean(t.avatar_url) &&
                  (hasVideo || Boolean(safeMediaUrl(t.photo_url)));
                return (
                <article
                  key={t.id}
                  className="testimonial-card"
                  style={{ flexBasis: `${100 / testimonialsData.length}%` }}
                  aria-hidden={idx !== activeTestimonialIndex}
                >
                  <div className="testimonial-media">
                    {hasVideo ? (
                      <>
                        {playingTestimonialIndex !== idx &&
                          (vPoster ? (
                            <div className="testimonial-poster" aria-hidden="true">
                              <img className="testimonial-poster-image" src={vPoster} alt="" />
                            </div>
                          ) : (
                            <div className="testimonial-media-empty" aria-hidden="true" />
                          ))}
                        {playingTestimonialIndex !== idx && (
                          <button
                            type="button"
                            className="testimonial-play-button"
                            aria-label="Play testimonial video"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTestimonialModal(idx);
                            }}
                          >
                            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <path d="M8 5v14l11-7z" fill="currentColor" />
                            </svg>
                          </button>
                        )}
                        <video
                          key={vSrc}
                          className={`testimonial-video ${playingTestimonialIndex === idx ? 'is-playing' : ''}`}
                          ref={(el) => {
                            testimonialVideoRefs.current[idx] = el;
                          }}
                          preload="metadata"
                          poster={vPoster}
                          aria-label={testimonialVideoAriaLabel(t)}
                          playsInline
                          onPlay={() => setPlayingTestimonialIndex(idx)}
                          onPause={() =>
                            setPlayingTestimonialIndex((cur) => {
                              if (cur === idx) return null;
                              return cur;
                            })
                          }
                          onClick={() => {
                            if (suppressTestimonialClickRef.current) return;
                            toggleTestimonialVideo(idx);
                          }}
                          onDoubleClick={() => {
                            suppressTestimonialClickRef.current = true;
                            window.setTimeout(() => {
                              suppressTestimonialClickRef.current = false;
                            }, 250);
                            openTestimonialModal(idx);
                          }}
                        >
                          <source src={vSrc} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </>
                    ) : vPoster ? (
                      <img
                        src={vPoster}
                        alt=""
                        className="testimonial-media-cover"
                      />
                    ) : (
                      <div className="testimonial-media-empty" aria-hidden="true" />
                    )}
                  </div>
                  <div className="testimonial-body">
                    {showBodyAvatar && (
                      <img
                        src={safeMediaUrl(t.avatar_url)}
                        alt={t.name}
                        className="testimonial-avatar"
                      />
                    )}
                    <p className="testimonial-quote">&quot;{t.quote || t.review}&quot;</p>
                    <div className="testimonial-name">
                      {t.name}
                      {t.is_verified !== false && (
                        <span className="testimonial-verified" aria-hidden="true">●</span>
                      )}
                    </div>
                    <div className="testimonial-time">{t.time}</div>
                    <div className="testimonial-stars" aria-hidden="true">
                      {'★'.repeat(Math.min(5, Math.max(0, Number(t.rating) || 5)))}
                      {'☆'.repeat(Math.max(0, 5 - Math.min(5, Number(t.rating) || 5)))}
                    </div>
                    
                  </div>
                </article>
                );
              })}
            </div>

            <div className="testimonials-dots" role="tablist" aria-label="Testimonial slides">
              {testimonialsData.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`dot ${idx === activeTestimonialIndex ? 'active' : ''}`}
                  aria-label={`Go to testimonial ${idx + 1}`}
                  aria-selected={idx === activeTestimonialIndex}
                  onClick={() => goToTestimonial(idx)}
                />
              ))}
            </div>
          </div>

          <div className="testimonials-join" aria-hidden="true">
            <div className="testimonials-join-text">Join 500+ happy families</div>
          </div>
        </div>
      </section>

      {testimonialModalIndex != null && (() => {
        const modalT = testimonialsData[testimonialModalIndex];
        const modalSrc = modalT ? getTestimonialVideoUrl(modalT) : '';
        if (!modalSrc) return null;
        return (
        <div
          className="testimonial-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Testimonial video"
          onClick={closeTestimonialModal}
        >
          <div className="testimonial-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="testimonial-modal-close"
              aria-label="Close video"
              onClick={closeTestimonialModal}
            >
              ×
            </button>
            <video
              key={`testimonial-modal-video-${testimonialModalIndex}-${modalSrc}`}
              className="testimonial-modal-video"
              controls
              autoPlay
              playsInline
              poster={safeMediaUrl(modalT?.photo_url) || safeMediaUrl(modalT?.avatar_url) || undefined}
            >
              <source src={modalSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
        );
      })()}

      {/* CTA */}
      <section className="cta" id="schedule-tour">
        <div className="cta-inner">
          <h2 className="cta-title">Join Our Preschool & Afterschool Family</h2>
          <p className="cta-desc">Give your child the best beginning they deserve. Schedule a tour today and see why families love The David Foster Preschool & Afterschool Academy.</p>
          <div className="cta-actions">
            <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </Link>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>

          <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default HomePage;
